import React, { useCallback, useState, memo, useEffect } from 'react';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button/entry-points/EditorAppearanceButtonNext.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	COVER_IMAGE_FIXED_HEIGHT,
	COVER_IMAGE_FIXED_WIDTH,
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE,
} from '@confluence/page-cover-picture/entry-points/pageCoverPictureConstants.ts';
import { getDynamicCoverImageHeight } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { useEditorWidthObserver } from '@confluence/editor-features/entry-points/useEditorWidthObserver.ts';
import { EditorContentModeButton } from '@confluence/editor-content-mode-button/entry-points/EditorContentModeButton.ts';

import { CoverPictureAndButton } from './CoverPictureAndButton.tsx';
import { EmojiButton } from './EmojiButton.tsx';
import { ContentStateHoverButton } from './ContentStateHoverButton.tsx';
import { AlignmentButtonContainer } from './AlignmentButtonContainer.tsx';
import type {
	FloatingToolbarButtonStyleType,
	ToolbarAboveTitleProps,
} from './titleareaContentComponentsTypes.ts';
import { SuggestTitleButton } from './SuggestTitleButton.tsx';
import { AlignmentComponent } from './AlignmentComponent.tsx';
import { DictationButton } from './DictationButton.tsx';

const toolbarAboveTitleBoxStylesBase = xcss({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	marginBottom: 'space.200',
});

const toolbarAboveTitleLeftAlignmentStyles = xcss({
	alignItems: 'start',
});

const toolbarAboveTitleCenterAlignmentStyles = xcss({
	alignItems: 'center',
});

const toolbarAboveTitleHiddenStyles = xcss({
	overflowX: 'hidden',
});

const coverPictureAndButtonFixedWidthHeightStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	height: `${COVER_IMAGE_FIXED_HEIGHT}px`,
});

const coverPictureAndButtonFullWidthHeightStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	height: `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}`,
});

const toolbarStyle = xcss({
	// @ts-ignore TODO: refactor to align this to the design system
	marginTop: '-22px',
	width: '100%',
	marginBottom: 'space.0',
});

const editorAppearanceButtonBoxXcss = xcss({
	marginRight: 'space.025',
});

const toolbarListItemStyles = xcss({
	margin: 'space.0',
	padding: 'space.0',
});

const rightControlsGroupXcss = xcss({
	display: 'flex',
	columnGap: 'space.0',
});

type ToolbarButtonsWrapperProps = {
	isUploadingPicture: boolean;
	hasCoverPicture: boolean;
	shouldShowToolbar: boolean;
	isTitleCenterAligned?: boolean;
	hasEmoji?: boolean;
	isReadOnly?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButtonsWrapper = styled.ul<ToolbarButtonsWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- needed to override wiki overrides for list styles
	listStyle: 'none !important',
	margin: 0,
	background: token('elevation.surface'),
	position: 'relative',
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignSelf: ({ hasCoverPicture, isUploadingPicture, isTitleCenterAligned }) =>
		(hasCoverPicture || isUploadingPicture) && isTitleCenterAligned ? 'center' : 'initial',
	width: 'fit-content',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	padding: token('space.050'),
	paddingLeft: 0,
	zIndex: 200,
	display: 'flex',

	// Use opacity to control toolbar visibility on hover - this allows mouse interaction while keeping its space in the UI
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	opacity: ({ shouldShowToolbar }) => (shouldShowToolbar ? 1 : `var(--toolbar-opacity)`),

	// We need to set visibility: hidden when isReadOnly to disallow user interaction while keeping element's space in the UI
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	visibility: ({ isReadOnly }) => (isReadOnly ? 'hidden' : 'visible'),
	'@media only screen and (max-width: 400px)': {
		// eslint-disable-next-line confluence-feature-gating/no-module-level-eval
		visibility: 'hidden',
	},

	transition: 'opacity 0.1s ease-in',

	// This mirrors the stylistic choice found in `platform/packages/editor/editor-common/src/styles/shared/plugins.ts`
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not([disabled])::after': {
			border: token('space.0'),
		},
	},
	columnGap: token('space.050'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginTop: ({ hasCoverPicture, hasEmoji }) =>
		hasCoverPicture ? (hasEmoji ? token('space.600') : token('space.300')) : undefined,
});

// Fallback wrapper when FG is off: static non-token border/radius values
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButtonsWrapperFallback = styled.ul<ToolbarButtonsWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- needed to override wiki overrides for list styles
	listStyle: 'none !important',
	margin: 0,
	background: token('elevation.surface'),
	position: 'relative',
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignSelf: ({ hasCoverPicture, isUploadingPicture, isTitleCenterAligned }) =>
		(hasCoverPicture || isUploadingPicture) && isTitleCenterAligned ? 'center' : 'initial',
	width: 'fit-content',
	borderRadius: '3px',
	padding: token('space.050'),
	paddingLeft: 0,
	zIndex: 200,
	display: 'flex',

	// Use opacity to control toolbar visibility on hover - this allows mouse interaction while keeping its space in the UI
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	opacity: ({ shouldShowToolbar }) => (shouldShowToolbar ? 1 : `var(--toolbar-opacity)`),

	// We need to set visibility: hidden when isReadOnly to disallow user interaction while keeping element's space in the UI
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	visibility: ({ isReadOnly }) => (isReadOnly ? 'hidden' : 'visible'),
	'@media only screen and (max-width: 400px)': {
		// eslint-disable-next-line confluence-feature-gating/no-module-level-eval
		visibility: 'hidden',
	},

	transition: 'opacity 0.1s ease-in',

	// This mirrors the stylistic choice found in `platform/packages/editor/editor-common/src/styles/shared/plugins.ts`
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not([disabled])::after': {
			border: 'none',
		},
	},
	columnGap: token('space.050'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginTop: ({ hasCoverPicture, hasEmoji }) =>
		hasCoverPicture ? (hasEmoji ? token('space.600') : token('space.300')) : undefined,
});

const floatingToolbarButtonStyle: FloatingToolbarButtonStyleType = {
	height: '32px',
	alignItems: 'center',
};

export const ToolbarAboveTitle = memo(
	({
		contentId,
		userOffline,
		isUnpublishedDraft,
		isTitleDisabled,
		isCoverPictureModalOpen,
		hasCoverPicture,
		isUploadingPicture,
		isAddHeaderImageButtonFocused,
		setSelectedEmoji,
		onEmojiPickerLoaded,
		onUpdateStartEmoji,
		onUpdateEndEmoji,
		isEmojiButtonFocused,
		hasEmoji,
		contentType,
		isPublishingContent,
		hasContentState,
		isSpaceOverview,
		contentStateState,
		isTitleCenterAligned,
		alignmentButtonState,
		coverPictureState,
		isSecondEmojiPickerOpen,
		flags,
		isReadOnly,
		isEditorEmpty,
		coverPictureWidth,
		spaceKey,
		contentComponentsDisabled,
		isDraftLockedForEditing,
		isStatusDisconnected,
		coverImageWidthCached,
		isUnsplash,
		hideSuggestedTitle = false,
		hideContentState = false,
		hideEditorAppearanceButton = false,
		hideAlignmentButton = false,
		hideContentModeButton = false,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		aiHeaderImageError,
		generateAIHeaderImage,
		setAIHeaderImageStatus,
		autoPageEmojiCohort,
		emojiTitleDiscovered,
		isSmartCreate,
		onSuggestTitleClickedRef,
	}: ToolbarAboveTitleProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

		const isLivePage = useIsCurrentPageLive();

		const hasCoverPictureOrUploadingPicture = hasCoverPicture || isUploadingPicture;

		// Hide toolbar when live page is rendered in preview panel
		const shouldHideToolbarInPreviewPanel = isLivePage && isInPreviewPanel();

		const getToolbarTestId = () => {
			if (shouldHideToolbarInPreviewPanel) {
				return 'toolbar-above-title-wrapper-hidden';
			}
			if (isTitleCenterAligned) {
				return 'toolbar-above-title-wrapper-centered';
			}
			return 'toolbar-above-title-wrapper';
		};

		const isServerPlaceholder = useRenderServerPlaceholder(true);

		const isFixedWidth =
			coverImageWidthCached && isServerPlaceholder
				? coverImageWidthCached === COVERPICTUREWIDTH.FIXED
				: coverPictureWidth === COVERPICTUREWIDTH.FIXED;

		const [isContentStateButtonFocused, setIsContentStateButtonFocused] = useState(false);
		const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState(false);
		const [isAlignmentDropdownOpen, setIsAlignmentDropdownOpen] = useState(false);
		const [isAppearanceDropdownOpen, setIsAppearanceDropdownOpen] = useState(false);

		// Tracks if any of the pickers, dropdowns, or hover buttons are open or focused
		const onlineShouldShowToolbar =
			isSecondEmojiPickerOpen ||
			isAddHeaderImageButtonFocused ||
			isEmojiButtonFocused ||
			isContentStateButtonFocused ||
			isContentStateMenuOpen ||
			isCoverPictureModalOpen ||
			isAlignmentDropdownOpen ||
			(isAppearanceDropdownOpen &&
				(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)));

		const [shouldShowToolbarPrevOnlineValue, setShouldShowToolbarPrevOnlineValue] =
			useState<boolean>(onlineShouldShowToolbar);

		useEffect(() => {
			if (!userOffline) {
				setShouldShowToolbarPrevOnlineValue(onlineShouldShowToolbar);
			}
		}, [userOffline, onlineShouldShowToolbar]);

		const shouldShowToolbar =
			!isReadOnly && (userOffline ? shouldShowToolbarPrevOnlineValue : onlineShouldShowToolbar);

		useEffect(() => {
			return () => {
				// Whenever we leave the editor, we need to clean up and reset the content state container values on unmount,
				// otherwise when the user opens editor, previous values are incorrectly persisted (could also do this in breadcrumbs but chose here)
				void contentStateState.resetToDefaults();
				void coverPictureState.resetToDefaults();
				void alignmentButtonState?.resetToDefaults();
			};
		}, [contentStateState, coverPictureState, alignmentButtonState]);

		const editorAppearanceButton = (
			<EditorAppearanceButtonNext
				contentId={contentId}
				disabled={!!contentComponentsDisabled}
				isStatusDisconnected={!!isStatusDisconnected}
				setIsAppearanceDropdownOpen={setIsAppearanceDropdownOpen}
				isAppearanceDropdownOpen={isAppearanceDropdownOpen}
			/>
		);

		const { width } = useEditorWidthObserver({ isFixedWidth });
		const coverImageWidth = isFixedWidth ? COVER_IMAGE_FIXED_WIDTH : width;

		const dynamicCoverImageHeight = isFixedWidth
			? COVER_IMAGE_FIXED_HEIGHT
			: getDynamicCoverImageHeight(coverImageWidth);

		const [coverPictureAndButtonWrapperHeight, setCoverPictureAndButtonWrapperHeight] =
			useState(dynamicCoverImageHeight);

		const coverPicAndButton = (
			<CoverPictureAndButton
				contentId={contentId}
				contentType={contentType}
				isPublishingContent={isPublishingContent}
				userOffline={userOffline}
				hasCoverPicture={hasCoverPicture}
				isUploadingPicture={isUploadingPicture}
				hasContentState={hasContentState}
				isUnpublishedDraft={isUnpublishedDraft}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isFixedWidthImageOption={isFixedWidth}
				updateHeight={setCoverPictureAndButtonWrapperHeight}
				currentHeight={coverPictureAndButtonWrapperHeight}
				isReadOnly={isReadOnly}
				isDraftLockedForEditing={isDraftLockedForEditing}
				spaceKey={spaceKey}
				isUnsplash={isUnsplash}
				dynamicCoverImageHeight={dynamicCoverImageHeight}
				width={width}
				coverImageWidth={coverImageWidth}
				aiHeaderImageStatus={aiHeaderImageStatus}
				aiHeaderImageFileId={aiHeaderImageFileId}
				aiHeaderImageError={aiHeaderImageError}
				generateAIHeaderImage={generateAIHeaderImage}
				setAIHeaderImageStatus={setAIHeaderImageStatus}
				isSmartCreate={isSmartCreate}
			/>
		);

		const onContentStateUpdatedSubscription = useCallback(
			(_: string, data: any) => {
				const { contentStateUpdated } = data || {};
				try {
					const contentState = JSON.parse(contentStateUpdated?.value);
					const parsedContentState = contentState?.contentState;
					// JSON.parse returns numeric ids as numbers, but the rest of the codebase
					// uses string ids (matching the GraphQL ID scalar type). Coerce to string
					// to prevent strict equality mismatches in ContentStateMenuV2.
					if (
						parsedContentState?.id != null &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true)
					) {
						parsedContentState.id = String(parsedContentState.id);
					}
					void contentStateState.setContentState(parsedContentState);
				} catch (error) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXPERIENCES,
					});
					markErrorAsHandled(error);
				}
			},
			[contentStateState],
		);

		const onContentStateDeletedSubscription = useCallback(() => {
			void contentStateState.setContentState(null);
		}, [contentStateState]);

		useSubscriptionEvent({
			subscriberId: 'ToolbarAboveTitleContentStateUpdated',
			delta: 'contentStateUpdated',
			contentId,
			contentType: contentType || 'page',
			onDataReceived: onContentStateUpdatedSubscription,
		});

		useSubscriptionEvent({
			subscriberId: 'TitleAreaContentComponentsContentStateDeleted',
			delta: 'contentStateDeleted',
			contentId,
			contentType: contentType || 'page',
			onDataReceived: onContentStateDeletedSubscription,
		});

		const coverPicAndButtonAboveToolbar = (
			<Box
				xcss={[
					toolbarStyle,
					isFixedWidth
						? coverPictureAndButtonFixedWidthHeightStyle
						: coverPictureAndButtonFullWidthHeightStyle,
				]}
				testId="cover-pic-and-button-wrapper-above-toolbar"
				data-id="cover-pic-and-button-wrapper"
			>
				{coverPicAndButton}
			</Box>
		);

		const coverPicAndButtonInsideToolbar = (
			<div data-testid="cover-pic-and-button-wrapper-inside-toolbar">{coverPicAndButton}</div>
		);

		return (
			<Subscribe to={[AlignmentButtonContainer]}>
				{(alignmentButtonContainer: AlignmentButtonContainer) => {
					const ToolbarWrapper = fg('confluence_frontend_tokenize_border')
						? ToolbarButtonsWrapper
						: ToolbarButtonsWrapperFallback;
					return (
						<Box
							xcss={[
								toolbarAboveTitleBoxStylesBase,
								isTitleCenterAligned
									? toolbarAboveTitleCenterAlignmentStyles
									: toolbarAboveTitleLeftAlignmentStyles,
								shouldHideToolbarInPreviewPanel && toolbarAboveTitleHiddenStyles,
							]}
							testId={getToolbarTestId()}
							data-toolbar-visible={shouldShowToolbar ? 'true' : undefined}
						>
							{hasCoverPictureOrUploadingPicture ? coverPicAndButtonAboveToolbar : null}
							<ToolbarWrapper
								isUploadingPicture={isUploadingPicture}
								hasCoverPicture={hasCoverPicture}
								isTitleCenterAligned={isTitleCenterAligned}
								shouldShowToolbar={shouldShowToolbar}
								hasEmoji={hasEmoji}
								isReadOnly={isReadOnly}
								data-testid="toolbar-buttons-wrapper"
							>
								{!hideAlignmentButton && (
									<Box as="li" xcss={toolbarListItemStyles}>
										<AlignmentComponent
											alignmentButtonContainer={alignmentButtonContainer}
											userOffline={userOffline}
											isDraftLockedForEditing={isDraftLockedForEditing}
											createAnalyticsEvent={createAnalyticsEvent}
											flags={flags}
											contentType={contentType}
											alignmentButtonState={alignmentButtonState}
											coverPictureState={coverPictureState}
											contentId={contentId}
											setIsAlignmentDropdownOpen={setIsAlignmentDropdownOpen}
											isAlignmentDropdownOpen={isAlignmentDropdownOpen}
										/>
									</Box>
								)}

								<Box as="li" xcss={toolbarListItemStyles}>
									<EmojiButton
										contentId={contentId}
										userOffline={userOffline}
										isDraftLockedForEditing={isDraftLockedForEditing}
										isUnpublishedDraft={isUnpublishedDraft}
										isTitleDisabled={isTitleDisabled}
										setSelectedEmoji={setSelectedEmoji}
										onEmojiPickerLoaded={onEmojiPickerLoaded}
										onUpdateStartEmoji={onUpdateStartEmoji}
										onUpdateEndEmoji={onUpdateEndEmoji}
										floatingToolbarButtonStyle={floatingToolbarButtonStyle}
										contentType={contentType}
										autoPageEmojiCohort={autoPageEmojiCohort}
										emojiTitleDiscovered={emojiTitleDiscovered}
										isSmartCreate={isSmartCreate}
									/>
								</Box>

								{!hideContentState && !hasContentState && !isSpaceOverview && (
									<Box as="li" xcss={toolbarListItemStyles}>
										<ContentStateHoverButton
											contentId={contentId}
											hasContentState={hasContentState}
											contentType={contentType}
											userOffline={userOffline}
											setIsContentStateButtonFocused={setIsContentStateButtonFocused}
											setIsContentStateMenuOpen={setIsContentStateMenuOpen}
											floatingToolbarButtonStyle={floatingToolbarButtonStyle}
										/>
									</Box>
								)}
								{hasCoverPictureOrUploadingPicture ? null : (
									<Box as="li" xcss={toolbarListItemStyles}>
										{coverPicAndButtonInsideToolbar}
									</Box>
								)}
								{!hideSuggestedTitle && isAIEnabledForContent && (
									<Box as="li" xcss={toolbarListItemStyles}>
										<SuggestTitleButton
											isEditorEmpty={isEditorEmpty}
											floatingToolbarButtonStyle={floatingToolbarButtonStyle}
											userOffline={userOffline}
											isDraftLockedForEditing={isDraftLockedForEditing}
											onSuggestTitleClickedRef={onSuggestTitleClickedRef}
										/>
									</Box>
								)}
								{!hideContentModeButton &&
									(fg('platform_editor_content_mode_button_mvp') ||
										expValEquals('confluence_compact_text_format', 'isEnabled', true)) && (
										<Box as="li" xcss={toolbarListItemStyles}>
											<Box
												xcss={rightControlsGroupXcss}
												testId="title-toolbar-right-controls-group"
											>
												<EditorContentModeButton
													contentId={contentId}
													disabled={isDraftLockedForEditing}
													isStatusDisconnected={isStatusDisconnected}
												/>
												{!hideEditorAppearanceButton && (
													<Box testId="editor-appearance-button-wrapper">
														{editorAppearanceButton}
													</Box>
												)}
											</Box>
										</Box>
									)}
								{!fg('platform_editor_content_mode_button_mvp') &&
									!expValEquals('confluence_compact_text_format', 'isEnabled', true) &&
									!hideEditorAppearanceButton && (
										<Box as="li" xcss={toolbarListItemStyles}>
											<Box
												xcss={editorAppearanceButtonBoxXcss}
												testId="editor-appearance-button-wrapper"
											>
												{editorAppearanceButton}
											</Box>
										</Box>
									)}
								{expValEquals('confluence-dictation-button-on-pages', 'isEnabled', true) && (
									<Box as="li" xcss={toolbarListItemStyles}>
										<DictationButton
											userOffline={userOffline}
											isDraftLockedForEditing={isDraftLockedForEditing}
										/>
									</Box>
								)}
							</ToolbarWrapper>
						</Box>
					);
				}}
			</Subscribe>
		);
	},
);
ToolbarAboveTitle.displayName = 'ToolbarAboveTitle';
